/**
 * Status, filter, and display config for establishments.
 */
import {
  Building2,
  Shield,
  CheckCircle2,
  PauseCircle,
  Ban,
  CircleOff,
  Network,
  Star,
  Handshake,
  XCircle,
  CircleDashed,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import { ESTABLISHMENT_TYPE_CONFIG, type EstablishmentColorName } from '@/constants/shared-catalog';

export { ESTABLISHMENT_TYPE_CONFIG, type EstablishmentColorName };

export { BOOLEAN_SELECT_OPTIONS } from '@/constants/shared-catalog';

/** Badge + icon config for an establishment or prospection status. */
export interface EstablishmentStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge + icon config for public / private. */
export interface EstablishmentCategoryConfig {
  label: string;
  icon: LucideIcon;
  badgeColor: string;
  colorName: EstablishmentColorName;
}

/** Badge config for public / private. */
export const ESTABLISHMENT_CATEGORY_CONFIG: Record<string, EstablishmentCategoryConfig> = {
  public: {
    label: 'Public',
    icon: Building2,
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  private: {
    label: 'Privé',
    icon: Shield,
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
};

/** Badge + icon config for REP / REP+ / none. */
export interface EstablishmentPriorityNetworkConfig {
  label: string;
  icon: LucideIcon;
  badgeColor: string;
  colorName: EstablishmentColorName;
}

/** Badge config for each REP / REP+ / none value. */
export const ESTABLISHMENT_PRIORITY_NETWORK_CONFIG: Record<
  string,
  EstablishmentPriorityNetworkConfig
> = {
  none: {
    label: 'Aucun',
    icon: CircleOff,
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  rep: { label: 'REP', icon: Network, badgeColor: 'var(--color-2-nuance-1)', colorName: 'color-2' },
  rep_plus: {
    label: 'REP+',
    icon: Star,
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
};

/** Badge config for each establishment status. */
export const ESTABLISHMENT_STATUS_CONFIG: Record<string, EstablishmentStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  suspended: {
    label: 'Suspendu',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  closed: {
    label: 'Fermé',
    icon: Ban,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Badge config for each prospection status. */
export const PROSPECTION_STATUS_CONFIG: Record<string, EstablishmentStatusConfig> = {
  to_define: {
    label: 'À définir',
    icon: CircleDashed,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  en_negociation: {
    label: 'En négociation',
    icon: Handshake,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  valide: {
    label: 'Validé',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  refuse: {
    label: 'Refusé',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** {@link Select} options for editable prospection statuses. */
export const EDITABLE_PROSPECTION_STATUS_OPTIONS = [
  { value: 'en_negociation', label: 'En négociation' },
  { value: 'valide', label: 'Validé' },
  { value: 'refuse', label: 'Refusé' },
] as const;

/** Configure-panel order for establishment filter groups. */
export const ALL_FILTER_GROUP_IDS = [
  'establishment-types',
  'establishment-categories',
  'establishment-priority-networks',
  'establishment-qpv',
  'establishment-statuses',
  'establishment-prospection-statuses',
  'establishment-cities',
  'establishment-departments',
  'establishment-calendar',
  'establishment-search',
  'establishment-dates',
  'filter-managed-by',
  'establishment-convention-signed',
  'establishment-specialties',
  'establishment-homework-support',
  'establishment-boarding',
  'establishment-partnership-period',
  'establishment-renewal-next-year',
  'establishment-quotas',
] as const;

/** Filter group IDs for the establishments {@link DataTable} configure panel. */
export type FilterGroupId = (typeof ALL_FILTER_GROUP_IDS)[number];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'establishment-types': 'Type',
  'establishment-categories': 'Catégorie',
  'establishment-priority-networks': 'Réseau prioritaire',
  'establishment-qpv': 'QPV',
  'establishment-statuses': 'Statut',
  'establishment-prospection-statuses': 'Statut de prospection',
  'establishment-cities': 'Ville',
  'establishment-departments': 'Département',
  'establishment-calendar': 'Type de calendrier',
  'establishment-search': 'Recherche',
  'establishment-dates': 'Date de création',
  'filter-managed-by': 'Géré par',
  'establishment-specialties': 'Spécialités',
  'establishment-homework-support': 'Devoirs Faits',
  'establishment-boarding': 'Internat',
  'establishment-partnership-period': 'Période de partenariat',
  'establishment-renewal-next-year': 'Reconduction N+1',
  'establishment-convention-signed': 'Convention signée',
  'establishment-quotas': 'Quotas',
};

import type {
  EstablishmentCategory,
  EstablishmentPriorityNetwork,
  EstablishmentType,
  EstablishmentStatus,
  ProspectionStatus,
  PartnerRelationshipQuality,
} from '@/types/establishment.types';

/** Label + color for partner relationship quality. */
export interface PartnerRelationshipQualityConfig {
  label: string;
  badgeColor: string;
}

/** Badge config for each partner relationship quality. */
export const PARTNER_RELATIONSHIP_QUALITY_CONFIG: Record<
  PartnerRelationshipQuality,
  PartnerRelationshipQualityConfig
> = {
  very_bad: { label: 'Très mauvaise', badgeColor: 'rgb(var(--color-error))' },
  bad: { label: 'Mauvaise', badgeColor: 'rgb(var(--status-warning))' },
  average: { label: 'Moyenne', badgeColor: 'var(--color-2-nuance-1)' },
  good: { label: 'Bonne', badgeColor: 'rgb(var(--status-success))' },
  very_good: { label: 'Très bonne', badgeColor: 'var(--color-1-nuance-1)' },
};

/** {@link Select} options for partner relationship quality. */
export const PARTNER_RELATIONSHIP_QUALITY_OPTIONS = [
  { value: 'very_bad', label: 'Très mauvaise' },
  { value: 'bad', label: 'Mauvaise' },
  { value: 'average', label: 'Moyenne' },
  { value: 'good', label: 'Bonne' },
  { value: 'very_good', label: 'Très bonne' },
];

/** {@link Select} options for involvement level (1–5). */
export const INVOLVEMENT_LEVEL_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
];

/**
 * UI labels vs API/DB property names:
 * | label key                  | API field           | DB column                       | Follow-up col  |
 * | vscToRecruit               | volunteerQuota      | establishments.volunteer_quota  | VSC follow-up  |
 * | studentsToAccompany        | studentQuota        | establishments.student_quota    | Students follow-up |
 * | volunteersToRecruit        | volunteersToRecruit | establishments.volunteers_to_recruit | Tutors follow-up |
 * | volunteersPerEstablishment | tutorCount (read-only)| active tutors linked to establishment |
 *
 * KPI targets are entered per establishment via create/edit forms.
 * No bulk JSON/DB backfill for pilot targets — coordinators set Engagement values.
 */
export const ENGAGEMENT_FIELD_LABELS = {
  vscToRecruit: 'Nbre VSC à recruter',
  studentsToAccompany: "Nbre d'élèves à accompagner",
  volunteersToRecruit: 'Nbre de bénévoles à recruter',
  volunteersPerEstablishment: 'Nbre de bénévoles par établissement',
  maxPastDays: 'Jours de saisie rétroactive',
} as const;

/** Column labels and tooltips for establishment follow-up KPIs. */
export const ESTABLISHMENT_SUIVI_COLUMNS = {
  students: {
    label: 'Suivi Élèves',
    tooltip: "Nombre d'élèves réels par rapport à l'objectif défini.",
  },
  tutors: {
    label: 'Suivi Tuteurs',
    tooltip: "Nombre de tuteurs réels par rapport à l'objectif défini.",
  },
  vsc: {
    label: 'Suivi VSC',
    tooltip: "Nombre de VSC réels par rapport à l'objectif défini.",
  },
} as const;

/** Column label and tooltip for days from negotiation to validation. */
export const ESTABLISHMENT_OPENING_DELAY_COLUMN = {
  label: "Délai d'ouverture",
  tooltip:
    'Nombre de jours entre le passage en « En négociation » et la validation. Affiché uniquement pour les établissements validés.',
} as const;

/** Establishment list filter state. */
export interface EstablishmentFilters {
  types: EstablishmentType[];
  categories: EstablishmentCategory[];
  priorityNetworks: EstablishmentPriorityNetwork[];
  isQpv?: boolean | null;
  statuses: EstablishmentStatus[];
  prospectionStatuses: ProspectionStatus[];
  cities?: string[];
  departments?: string[];
  calendarTypes?: string[];
  searchQuery?: string;
  selectedEstablishmentId?: string;
  quotaMin?: number;
  quotaMax?: number;
  createdFrom?: Date;
  createdTo?: Date;
  coordinatorId?: string;
  isProspectionValidated?: boolean | null;
  specialtyIds?: string[];
  hasDevoirsFaits?: boolean | null;
  hasInternat?: boolean | null;
  renewalNextYear?: boolean | null;
  conventionSigned?: boolean | null;
  partnershipDateFrom?: Date;
  partnershipDateTo?: Date;
}

/** Default establishment list filters. */
export const DEFAULT_ESTABLISHMENT_FILTERS: EstablishmentFilters = {
  types: [],
  categories: [],
  priorityNetworks: [],
  isQpv: undefined,
  statuses: [],
  prospectionStatuses: [],
  cities: [],
  departments: [],
  calendarTypes: [],
  searchQuery: '',
  selectedEstablishmentId: undefined,
  quotaMin: undefined,
  quotaMax: undefined,
  createdFrom: undefined,
  createdTo: undefined,
  coordinatorId: undefined,
  isProspectionValidated: undefined,
  specialtyIds: [],
  hasDevoirsFaits: undefined,
  hasInternat: undefined,
  renewalNextYear: undefined,
  conventionSigned: undefined,
  partnershipDateFrom: undefined,
  partnershipDateTo: undefined,
};

/** Establishment types that support class management. */
export const ESTABLISHMENT_TYPES_WITH_CLASSES = new Set([
  'primaire',
  'college',
  'lycee',
  'lycee_polyvalent',
  'professionnel',
  'other',
]);

/** Legacy contact field names kept for form compatibility. */
export const LEGACY_CONTACT_FIELDS = ['contactName', 'contactEmail', 'contactPhone'] as const;

/** Map a `{ label }` config record to `{ value, label }` select options. */
function toSelectOptions(config: Record<string, { label: string }>) {
  return Object.entries(config).map(([value, item]) => ({ value, label: item.label }));
}

/** {@link Select} options for establishment type. */
export const ESTABLISHMENT_TYPE_FORM_OPTIONS = toSelectOptions(ESTABLISHMENT_TYPE_CONFIG);
/** {@link Select} options for establishment status. */
export const ESTABLISHMENT_STATUS_FORM_OPTIONS = toSelectOptions(ESTABLISHMENT_STATUS_CONFIG);
/** {@link Select} options for public / private. */
export const ESTABLISHMENT_CATEGORY_FORM_OPTIONS = toSelectOptions(ESTABLISHMENT_CATEGORY_CONFIG);
/** {@link Select} options for REP / REP+ / none. */
export const ESTABLISHMENT_PRIORITY_NETWORK_FORM_OPTIONS = toSelectOptions(
  ESTABLISHMENT_PRIORITY_NETWORK_CONFIG,
);
/** React Query keys invalidated after an establishment create / save. */
export const ESTABLISHMENT_FORM_QUERY_KEYS = {
  list: ['establishments'] as const,
  one: (id: string) => ['establishments', id] as const,
  classrooms: (id: string) => ['classrooms', id] as const,
  schoolClasses: (id: string) => ['school-classes', id] as const,
};
